import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Play, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const monthName = (m) => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m-1];

export default function PayrollPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/payroll/runs"); setRows(data); }
    catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runPayroll = async () => {
    if (!confirm(`Run payroll for ${monthName(month)} ${year}? Every active employee will be paid.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/payroll/runs", { month, year });
      toast.success(`Paid ${data.employee_count} employees · ${fmtMoney(data.total_net)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to run payroll"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <PageHeader
        testid="page-payroll"
        eyebrow="Cycles"
        title="Payroll runs."
        description="Every payroll cycle produces a payslip for every active employee. One button, one afternoon."
      />

      <div className="p-8 space-y-8">
        <div className="border border-neutral-950 bg-neutral-950 text-white p-8 grid md:grid-cols-3 gap-6 items-end">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30]">New cycle</div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2">Run payroll.</div>
            <p className="text-sm text-neutral-400 mt-2">Choose month and year. Each active employee's salary structure is applied.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-2">Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="run-month"
                className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-700 outline-none focus:border-white font-mono text-sm text-white">
                {[...Array(12)].map((_, i) => (
                  <option key={i+1} value={i+1}>{monthName(i+1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-2">Year</label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="run-year"
                className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-700 outline-none focus:border-white font-mono text-sm text-white">
                {[2024,2025,2026,2027].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div>
            <button onClick={runPayroll} disabled={running} data-testid="btn-run-payroll"
              className="w-full inline-flex items-center justify-between gap-2 bg-white text-neutral-950 px-5 py-3.5 font-mono text-sm hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-60">
              {running ? "Running…" : "Run this cycle"}
              <Play className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="runs-table">
            <thead className="bg-[#FAFAFA] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Employees</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Net paid</th>
                <th className="px-4 py-3">Run at</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-neutral-500 font-mono">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-neutral-500 font-mono">No payroll runs yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-[#FAFAFA] transition-colors" data-testid={`row-run-${r.id}`}>
                  <td className="px-4 py-3 font-semibold">{monthName(r.month)} {r.year}</td>
                  <td className="px-4 py-3 font-mono">{r.employee_count}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtMoney(r.total_gross)}</td>
                  <td className="px-4 py-3 font-mono text-right font-semibold text-[#002FA7]">{fmtMoney(r.total_net)}</td>
                  <td className="px-4 py-3 font-mono text-neutral-500 text-xs">{r.created_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/app/payslips?run=${r.id}`} data-testid={`view-slips-${r.id}`} className="inline-flex items-center gap-1 font-mono text-xs hover:text-[#002FA7] transition-colors">
                      View payslips <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
